import { useEffect, useState } from 'react';
import SidePanel from '../../components/SidePanel';
import { AlertTriangle } from 'lucide-react';
import { fmtKc } from './PokladnaPrehledTab';

const ACCENT = '#3B82F6';

/** `embedded` — viz FlekInspector: vykreslí jen obsah bez vlastního SidePanelu. */
export default function VyuctovaniInspector({ smenaId, isDark, onClose, embedded = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

    useEffect(() => {
        if (!smenaId) return;
        setLoading(true);
        fetch(`/api/finance/pokladna/vyuctovani/${smenaId}`, { credentials: 'same-origin' })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(setData).catch(console.error).finally(() => setLoading(false));
    }, [smenaId]);

    const smena = data?.smena;
    const v = data?.vyhodnoceni;
    const ceka = smena?.stav === 'ceka_na_schvaleni';

    const header = (
        <div className="flex-1">
            <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Vyúčtování smeny #{smenaId}</div>
            <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {smena?.zamestnanec ? `${smena.zamestnanec.jmeno} ${smena.zamestnanec.prijmeni}` : '—'} · {smena?.datum}
            </div>
        </div>
    );

    const telo = (
        <>
            {/* Tělo se kreslí, jen když v odpovědi opravdu je směna i vyhodnocení.
                Dřív stačilo, že `data` nejsou null — a protože se dál sahá na
                `smena.stav` a `v.*` bez pojistky, useknutá odpověď shodila celý
                panel na bílo místo toho, aby řekla, že se nic nenačetlo. */}
            {loading ? <div className="p-6 text-sm" style={{ color: 'var(--text-tertiary)' }}>Načítání…</div> : (!data || !smena || !v) ? (
                <div className="p-6 text-sm" style={{ color: 'var(--text-tertiary)' }}>Vyúčtování se nepodařilo načíst.</div>
            ) : (
                <div className="p-5 space-y-4 text-xs">
                    {/* Stav banner */}
                    <div className="rounded-lg p-3 text-sm font-medium" style={{ background: ceka ? '#f59e0b22' : '#10b98122', color: ceka ? '#f59e0b' : '#10b981' }}>
                        {ceka ? '⏳ Čeká na schválení manažerem' : `✓ Stav: ${smena.stav}`}
                    </div>

                    {/* Souhrn objednávek */}
                    <div className="rounded-lg p-3" style={{ background: 'var(--surface-base)' }}>
                        <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Souhrn objednávek</div>
                        <Row k={`Tržba celkem (A) — ${v.pocet_objednavek?.celkem ?? 0} obj.`} v={fmtKc(v.A_trzba_celkem)} bold />
                        <Row k={`Hotovost (F) — ${v.pocet_objednavek?.hotovost ?? 0}`} v={fmtKc(v.F_hotovostni_trzba)} />
                        <Row k={`Karta (G) — ${v.pocet_objednavek?.karta ?? 0}`} v={fmtKc(v.G_kartova_trzba)} />
                        <Row k={`Online (B) — ${v.pocet_objednavek?.online ?? 0}`} v={fmtKc(v.B_online)} />
                        <Row k="Spropitné z online (C)" v={fmtKc(v.C_dysko_online)} />
                    </div>

                    {/* Vstupní hodnoty od rozvozce */}
                    {(v.D_uzaverka_terminalu !== undefined || v.E_hotovost_v_pen !== undefined) && (
                        <div className="rounded-lg p-3" style={{ background: 'var(--surface-base)' }}>
                            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Co rozvozce zadal</div>
                            <Row k="Uzávěrka terminálu (D)" v={fmtKc(v.D_uzaverka_terminalu)} />
                            <Row k="Hotovost v peněžence (E)" v={fmtKc(v.E_hotovost_v_pen)} />
                        </div>
                    )}

                    {/* Spočítané hodnoty — net dýško/manko nahoře (jediné, podle čeho
                        manažer rozhoduje řešení), pod ním znaménkový per-platba rozpad.
                        Model je odolný vůči záměně hotovost↔karta — jednotlivé řádky
                        karta/hotovost se můžou „přelévat", ale net je správný. */}
                    {v.net_dysko_manko_kc !== undefined && (
                        <div className="rounded-lg p-3" style={{ background: 'var(--surface-base)' }}>
                            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Vypočítáno</div>
                            {/* Net = součet znaménkových dýšek/mank. Klíčové číslo. */}
                            <Row
                                k="Net dýško / manko"
                                v={
                                    <strong style={{
                                        color: Number(v.net_dysko_manko_kc) < 0 ? '#ef4444' : '#10b981',
                                        fontSize: 14,
                                    }}>
                                        {Number(v.net_dysko_manko_kc) >= 0 ? '+' : '−'}{fmtKc(Math.abs(Number(v.net_dysko_manko_kc) || 0))}
                                    </strong>
                                }
                                bold
                            />
                            <div className="my-1 border-t" style={{ borderColor: 'var(--border-default)' }} />
                            {/* Znaménkový per-platba rozpad — záporné = manko (červené), kladné = dýško. */}
                            <Row k="Hotovost (E − základ − tržba)" v={<SignedKc n={v.dysko_hotovost_kc} />} />
                            <Row k="Karta (terminál − systém)" v={<SignedKc n={v.dysko_karta_kc} />} />
                            <Row k="Online (spropitné)" v={<SignedKc n={v.dysko_online_kc} />} />
                            <div className="my-1 border-t" style={{ borderColor: 'var(--border-default)' }} />
                            <Row k="Hotovost k odevzdání (před zálohou)" v={<strong>{fmtKc(v.trzba_k_odevzdani_kc)}</strong>} bold />
                            {v.vyzaduje_komentar && (
                                <div className="mt-2 text-xs flex items-center gap-1" style={{ color: '#ef4444' }}>
                                    <AlertTriangle size={12} /> Netto manko nad eskalační limit ({fmtKc(v.manko_limit_eskalace_kc)}) — rozvozce dokládal foto + komentář
                                </div>
                            )}
                        </div>
                    )}

                    {smena.rozvozce_komentar && (
                        <div className="rounded-lg p-3" style={{ background: '#f59e0b15', border: '1px solid #f59e0b40' }}>
                            <div className="text-xs font-semibold mb-1" style={{ color: '#f59e0b' }}>Komentář rozvozce</div>
                            <div style={{ color: 'var(--text-primary)' }}>{smena.rozvozce_komentar}</div>
                        </div>
                    )}

                    {/* Schvalování tu není. Vyúčtování potvrzuje manažer
                        směny na place — v kartě svého cockpitu, kde zároveň
                        vidí, kolik si mají s kolegou předat. Druhé schválení
                        z kanceláře by rozhodovalo o penězích, které si mezi
                        sebou vyrovnali před hodinou; tady zůstává náhled
                        a historie. */}
                </div>
            )}
        </>
    );

    if (embedded) {
        return (
            <>
                <div className="px-5 py-3 border-b" style={{ borderColor: 'var(--border-default)' }}>{header}</div>
                {telo}
            </>
        );
    }

    return (
        <SidePanel open={true} onClose={onClose} title={header} isDark={isDark} isMobile={isMobile} width={580} modal={false} accent={ACCENT}>
            {telo}
        </SidePanel>
    );
}

function Row({ k, v, bold = false }) {
    return (
        <div className={'flex justify-between gap-2 py-0.5 ' + (bold ? 'font-semibold' : '')}>
            <span style={{ color: 'var(--text-tertiary)' }}>{k}</span>
            <span style={{ color: 'var(--text-primary)' }}>{v}</span>
        </div>
    );
}

// Znaménková částka — kladná = dýško (zelená), záporná = manko (červená).
function SignedKc({ n }) {
    const val = Number(n) || 0;
    if (Math.abs(val) < 0.005) return <span style={{ color: 'var(--text-primary)' }}>{fmtKc(0)}</span>;
    const negative = val < 0;
    return (
        <span style={{ color: negative ? '#ef4444' : '#10b981' }}>
            {negative ? '− ' : '+ '}{fmtKc(Math.abs(val))}
        </span>
    );
}
